/**
 * The geometric distribution under both textbook conventions.
 *
 * - "trials": X = the number of trials up to and including the first success,
 *   X = 1, 2, 3, …; P(X = k) = (1 − p)^(k − 1) p. Mean 1/p. (Devore, OpenStax, SciPy's
 *   `geom`.)
 * - "failures": Y = the number of failures before the first success, Y = 0, 1, 2, …;
 *   P(Y = k) = (1 − p)^k p. Mean (1 − p)/p. (R's `dgeom`/`pgeom`/`qgeom`, Wikipedia's
 *   second form.)
 *
 * They describe the same experiment: X = Y + 1. Everything here works from "m = the
 * number of failures before the first success" and shifts by the convention's first
 * value, so the two cannot drift apart.
 */

import type { DiscreteModel } from "@/lib/tool-kit/stats-distributions/discrete";

export type GeometricConvention = "trials" | "failures";

/** The smallest possible value: 1 trial, or 0 failures. */
export function geometricFirst(convention: GeometricConvention): number {
  return convention === "trials" ? 1 : 0;
}

/** The R argument for a value of the chosen convention: R's `dgeom(q, prob)` counts failures. */
export function toRFailures(
  k: number,
  convention: GeometricConvention
): number {
  return k - geometricFirst(convention);
}

/** (1 − p) raised to a non-negative number of failures, exact at p = 1. */
function failuresProbability(p: number, failures: number): number {
  if (failures <= 0) {
    return 1;
  }
  return p >= 1 ? 0 : Math.exp(failures * Math.log1p(-p));
}

/** 1 − (1 − p)^m through `expm1`, so a tiny p keeps its digits. */
function atLeastOneSuccess(p: number, attempts: number): number {
  if (attempts <= 0) {
    return 0;
  }
  return p >= 1 ? 1 : -Math.expm1(attempts * Math.log1p(-p));
}

export function geometricModel(
  p: number,
  convention: GeometricConvention
): DiscreteModel {
  const first = geometricFirst(convention);
  const variance = (1 - p) / (p * p);
  return {
    pmf: (k) =>
      Number.isInteger(k) && k >= first
        ? failuresProbability(p, k - first) * p
        : 0,
    cdf: (k) =>
      k < first ? 0 : atLeastOneSuccess(p, Math.floor(k) - first + 1),
    // P(X ≥ k) = P(no success in the first k − first attempts) = (1 − p)^(k − first).
    upper: (k) => failuresProbability(p, Math.ceil(k) - first),
    mean: convention === "trials" ? 1 / p : (1 - p) / p,
    variance,
    support: [first, Number.POSITIVE_INFINITY],
  };
}

/**
 * The median: the smallest value whose cumulative probability reaches 1/2. With p = 1
 * every trial succeeds, so it is the first value.
 */
export function geometricQuantile(
  q: number,
  p: number,
  convention: GeometricConvention
): number {
  const first = geometricFirst(convention);
  if (p >= 1 || q <= 0) {
    return first;
  }
  // Smallest m = failures + 1 attempts with 1 − (1 − p)^m ≥ q.
  const attempts = Math.ceil(Math.log1p(-q) / Math.log1p(-p) - 1e-12);
  return Math.max(1, attempts) - 1 + first;
}
